'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useContext, useMemo } from 'react';
import { Product, UpsellV2Style } from '@ordering/gql/graphql';
import { useInventory } from '@ordering/providers/InventoryProvider/InventoryProvider';
import { OrderStateContext } from '@ordering/stores/OrderStore/OrderStoreProvider';
import { ProductUpsellResult } from '@ordering/types/Upsells';
import { getCheckoutUpsellItems, getMenuUpsellItems, getProductUpsellItems, precomputeUpsells, UpsellTypes } from '@ordering/utils/upsells';
type UpsellsContextValue = {
  getUpsellsForProduct: (product: Product) => ProductUpsellResult[];
  getCheckoutUpsells: () => ProductUpsellResult[];
  getMenuUpsells: () => ProductUpsellResult[];
  rawUpsells: UpsellTypes.Upsell[];
};
const UpsellsContext = React.createContext<UpsellsContextValue>({} as UpsellsContextValue);
export type UpsellsProviderProps = {
  upsells: UpsellTypes.Upsell[];
  products: Product[];
  children: React.ReactNode;
};
export function UpsellsProvider(t0) {
  const $ = _c(23);
  const {
    upsells,
    products,
    children
  } = t0;
  const {
    inventory
  } = useInventory();
  const orderStore = useContext(OrderStateContext);
  let t1;
  if ($[0] !== orderStore) {
    t1 = () => orderStore?.getState().order.orderLines ?? [];
    $[0] = orderStore;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getBasket = t1;
  let t2;
  if ($[2] !== inventory || $[3] !== products || $[4] !== upsells) {
    t2 = precomputeUpsells(products, upsells, inventory);
    $[2] = inventory;
    $[3] = products;
    $[4] = upsells;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const precomputed = t2;
  let t3;
  if ($[6] !== getBasket || $[7] !== precomputed) {
    t3 = product => getProductUpsellItems(product, precomputed, getBasket());
    $[6] = getBasket;
    $[7] = precomputed;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getUpsellsForProduct = t3;
  let t4;
  if ($[9] !== getBasket || $[10] !== precomputed) {
    t4 = () => getCheckoutUpsellItems(precomputed, getBasket());
    $[9] = getBasket;
    $[10] = precomputed;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const getCheckoutUpsells = t4;
  let t5;
  if ($[12] !== getBasket || $[13] !== precomputed) {
    t5 = () => getMenuUpsellItems(precomputed, getBasket());
    $[12] = getBasket;
    $[13] = precomputed;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const getMenuUpsells = t5;
  let t6;
  if ($[15] !== getCheckoutUpsells || $[16] !== getMenuUpsells || $[17] !== getUpsellsForProduct || $[18] !== upsells) {
    t6 = {
      getUpsellsForProduct,
      getCheckoutUpsells,
      getMenuUpsells,
      rawUpsells: upsells
    };
    $[15] = getCheckoutUpsells;
    $[16] = getMenuUpsells;
    $[17] = getUpsellsForProduct;
    $[18] = upsells;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const value = t6;
  let t7;
  if ($[20] !== children || $[21] !== value) {
    t7 = <UpsellsContext.Provider value={value}>{children}</UpsellsContext.Provider>;
    $[20] = children;
    $[21] = value;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
const findByStyle = (upsells: ProductUpsellResult[], style: UpsellV2Style): ProductUpsellResult | null => upsells.find(upsell => upsell.style === style && upsell.items.length > 0) ?? null;
export const useProductUpsell = (product, style) => {
  const $ = _c(4);
  const context = useContext(UpsellsContext);
  let t0;
  if ($[0] !== context || $[1] !== product || $[2] !== style) {
    t0 = product ? findByStyle(context.getUpsellsForProduct?.(product) ?? [], style) : null;
    $[0] = context;
    $[1] = product;
    $[2] = style;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
export const useCheckoutUpsell = style => {
  const $ = _c(3);
  const context = useContext(UpsellsContext);
  let t0;
  if ($[0] !== context || $[1] !== style) {
    t0 = findByStyle(context.getCheckoutUpsells?.() ?? [], style);
    $[0] = context;
    $[1] = style;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

// Reads the cart at call time. The checkout nudge uses this on click so the modal matches the basket then.
export const useReadCheckoutUpsell = () => {
  const $ = _c(2);
  const context = useContext(UpsellsContext);
  let t0;
  if ($[0] !== context) {
    t0 = style => findByStyle(context.getCheckoutUpsells?.() ?? [], style);
    $[0] = context;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const useMenuUpsell = style => {
  const $ = _c(3);
  const context = useContext(UpsellsContext);
  let t0;
  if ($[0] !== context || $[1] !== style) {
    t0 = findByStyle(context.getMenuUpsells?.() ?? [], style);
    $[0] = context;
    $[1] = style;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const useRawUpsells = () => {
  const context = useContext(UpsellsContext);
  if (!context?.rawUpsells) {
    throw new Error("useRawUpsells must be used within an UpsellsProvider");
  }
  return context.rawUpsells;
};